import useCreateRedditReport from 'wb-reddit/app/routes/reports/create/useCreateRedditReport';
import { useMutation } from '@apollo/client';
import {
	CreateRedditReportDocument,
	CreateReportActionFormat,
	CreateReportActionOptionType,
	CreateReportActionType,
	CreateReportApiFiltersOptionsAction,
	CreateReportDateRangeType,
	CreateReportDateType,
	RedditReportsDocument,
	ReportDateType,
	UpdateRedditReportDocument,
} from 'wb-reddit/graphql/generated';
import { startCase } from 'lodash';
import dayjs from 'dayjs';
import useUserMetaData from 'wb-reddit/app/common/hooks/useUserMetaData';
import { DATE_PARSE_FORMAT, isReddit, isSpotify } from '@aleph/shared-components';
import {
	SpotifyReportsDocument,
	CreateSpotifyReportDocument,
	UpdateSpotifyReportDocument,
	ReportDateRangeType as SpotifyCreateReportDateRangeType,
	ReportActionType as SpotifyCreateReportActionType,
	ReportActionFormat as SpotifyCreateReportActionFormat,
	ReportActionOptionsTypes as SpotifyCreateReportActionOptionType,
} from 'wb-spotify/graphql/generated';
import useSupergridSettings from 'wb-spotify/app/routes/superGrid/hooks/useSupergridSettings';
import { SpotifyReportSchedule } from 'wb-spotify/app/common/utils';

const getReportScheduleFromSchedule = (schedule: ReportDateType, time: string) => {
	if (isSpotify()) {
		const selected = schedule as unknown as SpotifyReportSchedule;

		switch (selected) {
			case SpotifyReportSchedule.OnlyOnce:
				return time;
			case SpotifyReportSchedule.EveryMonday:
				return 'every_monday';
			case SpotifyReportSchedule.EveryWeekday:
				return 'every_weekday';
			case SpotifyReportSchedule.FirstOfTheMonth:
				return 'first_of_the_month';
			default:
				return 'only_once';
		}
	}

	switch (schedule) {
		case ReportDateType.Yesterday:
			return time;
		case ReportDateType.PreviousWeek:
		case ReportDateType.Biweekly:
			return 'every_monday';
		case ReportDateType.CurrentMonth:
		case ReportDateType.PreviousYear:
			return 'first_of_the_month';
	}

	return 'only_once';
};

const useCreateReportMutation = () => {
	const {
		schedule, breakdowns, metrics, filters, name, dates, reportId, time, userGroupIds, userIds, privateUserGroupIds,
	} = useCreateRedditReport();
	const { lifetimeStats } = useSupergridSettings();

	const [onCreate, { loading, error: creationError }] = useMutation(isReddit() ? CreateRedditReportDocument : CreateSpotifyReportDocument, {
		refetchQueries:      [isReddit() ? RedditReportsDocument : SpotifyReportsDocument],
		awaitRefetchQueries: true,
	});
	const [onUpdate, { loading: loadingUpdate, error: loadingError }] = useMutation(isReddit()
		? UpdateRedditReportDocument : UpdateSpotifyReportDocument);
	const userMetaData = useUserMetaData();

	const onCreateReport = async () => {
		if (!dates) {
			return;
		}

		const variables = isReddit() ? {
			// @ts-ignore: reportId will not be present on onCreate
			reportId,
			reportName:             name,
			reportApiDimensions:    breakdowns,
			reportApiMetrics:       metrics,
			reportActionType:       CreateReportActionType.Email,
			reportActionFormat:     CreateReportActionFormat.Csv,
			reportDateType:         (schedule as CreateReportDateType) ?? CreateReportDateType.Custom,
			reportSchedule:         getReportScheduleFromSchedule(schedule as ReportDateType, time),
			reportDateRangeOptions: (((schedule as CreateReportDateType) ?? CreateReportDateType.Custom) !== CreateReportDateType.Custom) ? undefined : {
				dateRangeType:  CreateReportDateRangeType.DateSchedule,
				dateRangeValue: [
					dayjs(dates.from)?.format(DATE_PARSE_FORMAT) ?? dayjs().subtract(1, 'week').format(DATE_PARSE_FORMAT),
					dayjs(dates.to)?.format(DATE_PARSE_FORMAT) ?? dayjs().format(DATE_PARSE_FORMAT),
				],
			},
			reportApiFiltersOptions: Object.keys(filters).map(key => ({
				action: CreateReportApiFiltersOptionsAction.Include,
				name:   key,
				filter: key,
				label:  startCase(key),
				value:  filters?.[key]?.[0]?.value ?? [],
			})),
			reportActionOptions: reportId ? undefined : {
				actionOptionType:  CreateReportActionOptionType.ActionEmails,
				actionOptionValue: [userMetaData?.userEmail ?? ''],
			},
			userIds,
			userGroupIds,
		} : {
			// @ts-ignore: reportId will not be present on onCreate
			reportId,
			// TODO check later maybe we can change it
			reportDataset:        'endpoint_spotify_statistics',
			reportDimensions:     breakdowns,
			// TODO later change in case if new filters
			reportFiltersOptions: [{
				name: 'userGroupIds', label: 'User Group ID', value: filters.userGroupId[0].value, filter: 'filter', action: 'INCLUDE',
			}],
			reportConditions:       null,
			reportMetrics:          metrics,
			reportHideZeroRows:     true,
			reportName:             name,
			reportDateRangeOptions: {
				dateRangeType:  SpotifyCreateReportDateRangeType.DateSchedule,
				dateRangeValue: [
					dayjs(dates.from)?.format(DATE_PARSE_FORMAT) ?? dayjs().subtract(1, 'week').format(DATE_PARSE_FORMAT),
					dayjs(dates.to)?.format(DATE_PARSE_FORMAT) ?? dayjs().format(DATE_PARSE_FORMAT),
				],
			},
			reportActionType:    SpotifyCreateReportActionType.Email,
			reportActionFormat:  SpotifyCreateReportActionFormat.Csv,
			reportActionOptions: reportId ? undefined : {
				actionOptionType:  SpotifyCreateReportActionOptionType.ActionEmails,
				actionOptionValue: [userMetaData?.userEmail ?? ''],
			},
			reportSchedule: getReportScheduleFromSchedule(schedule as ReportDateType, time),
			// first value is always the main user group, owner
			userGroupIds:   !reportId ? userGroupIds : [...new Set([filters.userGroupId[0].value[0], ...privateUserGroupIds])],
		};

		if (lifetimeStats) {
			if (variables?.reportDateType) {
				// @ts-ignore
				delete variables.reportDateType;
			}
			delete variables.reportDateRangeOptions;
		}

		await (reportId ? onUpdate : onCreate)(
			// @ts-ignore
			{ variables },
		);
	};

	return { onCreateReport, loading: loading || loadingUpdate, error: creationError || loadingError };
};

export default useCreateReportMutation;
